import { useEffect } from "react";
import { useIntl } from "react-intl";
import useActivationObj from "../../hooks/useActivationObj";
import Alert from "../alert/Alert";
import CheckIcon from "../icons/Check";
import BtnScanner from "../qrcode/BtnScanner";
import Button from "../ui/button";

type ObjectCodeFormProps = {
  inlineMode?: boolean;
  readonly?: boolean;
  objCode: string;
  onChange: (code: string) => void;
  onValid: (objID: number) => void;
}

export default function ObjectCodeForm ({
  inlineMode = true,
  objCode,
  readonly = false,
  onChange,
  onValid
}: ObjectCodeFormProps) {
  const intl = useIntl();

  const {
    activateObj,
    data: activatedObj,
    error: activationError,
    info: activationInfo,
    success: activationSuccessMessage,
    generalError: generalActivationError
  } = useActivationObj();
  
  useEffect(() => {
    if (activatedObj) {
      onValid(activatedObj.id)
    }
  }, [activatedObj, onValid])

  function extractCodeFromScanner(url: string) {
    const protocol = window.location.protocol;
    const host = window.location.host;
    const domainUrl = protocol.concat("//").concat(host);
    const objCode = url.replace(domainUrl + '/', '');
    onChange(objCode);
  }

  return (
    <>
      {/* ID BADGE */}
      <label className="mb-1.5 block text-shadow text-sm" htmlFor="objId">
        {intl.formatMessage({ id: 'forms.objectCode.label' })}
      </label>
      <div className="flex gap-5 items-center">
        <div className="w-full lg:w-1/2 flex gap-2.5 items-center">
          <input id="objId" type="text"
            placeholder={intl.formatMessage({ id: 'forms.objectCode.placeholder' })}
            autoComplete="off"
            value={objCode} onChange={(e) => onChange(e.target.value)}
            disabled={readonly}
            className="w-full input-sm"/>
          {
            !readonly && inlineMode &&
            <Button width="fit" size="medium" theme="black"
              className="px-[18px] flex items-center gap-2"
              onClick={() => activateObj(objCode)}>
              {intl.formatMessage({ id: 'forms.objectCode.validate' })} <CheckIcon className="w-3" />
            </Button>
          }
        </div>
        {
          !readonly && inlineMode &&
          <>
            <span className="text-sm text-primary">{intl.formatMessage({ id: 'common.or' })}</span>
            <BtnScanner onNewScan={(value) => extractCodeFromScanner(value)} />
          </>
        }
      </div>
      {/* Put on line the controls button if form has preview */}
      { !readonly && !inlineMode &&
        <div className="mt-2 flex flex-col sm:flex-row gap-1 sm:gap-2.5 items-start sm:items-center">
          <Button width="fit" size="medium" theme="black"
            className="px-[18px] flex items-center gap-2"
            onClick={() => activateObj(objCode)}>
            {intl.formatMessage({ id: 'forms.objectCode.validate' })} <CheckIcon className="w-3" />
          </Button>
          <span className="ms-10 sm:ms-0 text-sm leading-none text-primary">
            {intl.formatMessage({ id: 'common.or' })}
          </span>
          <BtnScanner onNewScan={(value) => extractCodeFromScanner(value)} />
        </div>
      }

      {/* ID BADGE INFO REQUEST */}
      <div className="lg:w-1/2 mb-8">
        {
          !readonly && (
            <>
              { activationSuccessMessage && <Alert className="my-[5px] w-fit" message={activationSuccessMessage} type="success"/> }
              {
                activationInfo &&
                <Alert
                  className="my-[5px] w-fit"
                  type="info" autoHidden={false}
                  message={activationInfo.message}
                  link={{
                    label: intl.formatMessage({ id: 'forms.objectCode.administer' }),
                    target: `../edit/${activationInfo.data.type}?id=${activationInfo.data.id}&objCode=${objCode}`
                  }}
                />
              }
              { activationError && <Alert className="my-[5px] w-fit" message={activationError} type="error"/> }
              { generalActivationError && <Alert className="my-[5px] w-fit" message={generalActivationError.message} type="error"/>}
            </>
          )
        }
      </div>
    </>
  )
}
